import React, { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridReadyEvent, themeQuartz } from 'ag-grid-community';
import Box from '@mui/material/Box';
import { useStyles } from 'components/reports/SchoolStatDetails/SchoolStatDetails.styles';
import { StatDetailsTable } from 'store/reports/reports.types';
import { useTranslator } from 'components/shared/hooks';
import { Paper } from 'components/shared/surfaces/Paper/Paper';
import { formatSpecialValues } from 'components/reports/SchoolStatDetails/helpers';
import { Tooltip } from 'components/shared/data/DataGrid/Tooltip/Tooltip';
import { toNumber } from 'lodash';

interface StatTreeProps {
  table: StatDetailsTable | null;
  name: string;
  setGridApi: React.Dispatch<any>;
}

export const StatTree = ({ table, name, setGridApi }: StatTreeProps) => {
  const classes = useStyles();
  const trans = useTranslator();
  const effectiveColumns = useMemo(() => {
    if (!table?.columns) return [];
    return table.columns;
  }, [table?.columns]);

  const { rowData, groupFields, valueColumns } = useMemo(() => {
    if (!table) {
      return { rowData: [], groupFields: [], valueColumns: [] };
    }

    const groupFields = effectiveColumns.filter((c) => c.group).map((c) => c.field);
    const valueColumns = effectiveColumns.filter((c) => !c.group);

    const flattenLeaves = (nodes: any[]): any[] => {
      const leaves: any[] = [];
      const walk = (node: any) => {
        if (!node) return;
        if (Array.isArray(node.children) && node.children.length > 0) {
          node.children.forEach(walk);
          return;
        }

        // Backend nodes are shaped as: { data: {...}, children: [...] }
        if (node.data && typeof node.data === 'object') {
          leaves.push(node.data);
          return;
        }

        // Fallback for already-flat row data
        if (typeof node === 'object') {
          leaves.push(node);
        }
      };

      nodes.forEach(walk);
      return leaves;
    };

    const leaves = flattenLeaves((table.data as any[]) || []);

    const numericFields = valueColumns
      .filter((c) => c.aggregationField !== null)
      .map((c) => c.field);

    const coercedLeaves = leaves.map((leaf) => {
      const next = { ...leaf };
      numericFields.forEach((field) => {
        const v = next[field];
        if (v === undefined || v === null || v === '') return;
        next[field] = toNumber(v);
      });
      return next;
    });

    return {
      rowData: formatSpecialValues(coercedLeaves, trans),
      groupFields,
      valueColumns,
    };
  }, [table, effectiveColumns, trans]);

  const onGridReady = (event: GridReadyEvent) => {
    setGridApi(event.api);
  };

  return (
    <Paper innerSpacing={2} square className={classes.paper}>
      <Box height={500}>
        <AgGridReact
          theme={themeQuartz}
          rowData={rowData}
          columnDefs={valueColumns.map((item) => ({
            ...item,
            aggFunc: item.aggregation,
            headerName: item.name,
            valueFormatter: ({ value }) => {
              if (item.aggregationField !== null) {
                if (value === undefined || value === null || value === '') return '0';
              }

              // Safety: if a value sneaks in as an object (e.g. AggregationValue),
              // convert it via toString.
              if (typeof value === 'object' && value !== null && typeof (value as any).toString === 'function') {
                return (value as any).toString();
              }

              return value;
            },
          })) as any}
          treeData
          autoGroupColumnDef={{
            headerName: name,
            suppressSizeToFit: true,
            maxWidth: 700,
            cellRendererParams: { suppressCount: true },
          }}
          icons={{
            groupExpanded: '<span class="statGroupIcon statGroupIcon--expanded" aria-hidden="true"></span>',
            groupContracted: '<span class="statGroupIcon statGroupIcon--contracted" aria-hidden="true"></span>',
          }}
          defaultColDef={{
            filter: true,
            resizable: true,
            tooltipComponent: 'gridTooltip',
            tooltipValueGetter: (params) => {
              return params.valueFormatted ? params.valueFormatted : params.value;
            },
          }}
          // Important: return a string[] path for treeData (root + intermediate levels).
          getDataPath={(row) => (groupFields.length ? groupFields.map((field) => row[field]) : [])}
          groupDefaultExpanded={-1}
          onGridReady={onGridReady}
          components={{
            gridTooltip: Tooltip,
          }}
          tooltipShowDelay={500}
          localeText={{
            selectAll: trans('COMMON.GRID.SELECT_ALL'),
            searchOoo: trans('COMMON.GRID.SEARCH'),
            blanks: trans('COMMON.GRID.BLANK'),
            noMatches: trans('COMMON.GRID.NO_MATCHES'),
            pinColumn: trans('COMMON.GRID.PIN_COLUMN'),
            pinLeft: trans('COMMON.GRID.PIN_LEFT'),
            pinRight: trans('COMMON.GRID.PIN_RIGHT'),
            noPin: trans('COMMON.GRID.NO_PIN'),
            autosizeThiscolumn: trans('COMMON.GRID.AUTOSIZE_THIS_COLUMN'),
            autosizeAllColumns: trans('COMMON.GRID.AUTOSIZE_ALL_COLUMNS'),
            resetColumns: trans('COMMON.GRID.RESET_COLUMNS'),
            expandAll: trans('COMMON.GRID.EXPAND_ALL'),
            collapseAll: trans('COMMON.GRID.COLLAPSE_ALL'),
          }}
        />
      </Box>
      <div className="pt-3 text-muted text-right">{trans('REPORTS.SCHOOL_STAT_DETAILS.FIXED_VALUES_EXPLANATION')}</div>
    </Paper>
  );
};
